// Чистая логика контрола изотерм: как из ответа бэка сделать сетку для
// marching squares и как выбрать диапазон/значение ползунка. Вынесено из вёрстки
// ради тестов — от границ ползунка зависит, дотянется ли капитан до нужной
// изотермы.

import type { SstGridResponse } from '../api'
import { ISOTHERM_STEP, snapToStep } from '../map/isotherms'
import type { SstGrid } from './marchingSquares'

// Диапазон по умолчанию, когда сетка ещё не загружена: SST Дальнего Востока
// круглый год укладывается в [-2, 30] °C. Ползунок должен быть осмысленным сразу.
const FALLBACK_MIN = -2
const FALLBACK_MAX = 30

// Единый инструмент «Изотермы + градиенты» (общий над разделами): фиксированный
// ПРОМЫСЛОВЫЙ диапазон, а не весь разброс SST во вьюпорте. Не data-driven
// (sliderBounds оставлена как чистая логика, но инструментом больше не
// используется).
//
// Низ опущен 7 → 5 → 0. Пятёрка была границей южного промысла (сайра/иваси), но
// район с тех пор дотянулся до Берингова моря, где в августе 2–4 °C: над такой
// водой инструмент просто не включался — ползунок не доезжал до температур,
// которые там есть. Ноль, а не −2: отрицательные встречаются лишь в кромке льда,
// и ради них растягивать шкалу нечестно — точность наведения ползунка важнее.
//
// Верх поднят 16 → 35 (решение владельца 13.10.2026): с Аравийским морем
// (27,5…30,9 °C в октябре) рабочая полоса перестала быть холодной, и при потолке
// 16 изотерма над тёплым морем не ложилась вовсе — ползунок стоял на краю.
export const ISO_MIN = 0
export const ISO_MAX = 35

/** Ответ эндпоинта → вход marchingSquares; null, если сетка пустая/битая. */
export function toContourGrid(resp: SstGridResponse | null | undefined): SstGrid | null {
  if (!resp || resp.rows === 0 || resp.cols === 0) return null
  if (resp.lat_top == null || resp.lon_left == null || resp.step == null) return null
  return {
    latTop: resp.lat_top,
    lonLeft: resp.lon_left,
    step: resp.step,
    // Оси порознь: сервер отдаёт шаг решётки РЕГИОНА, и на балтийской миле она
    // не квадратная (1/60° по широте, 1/36° по долготе). Старый ответ без этих
    // полей читается квадратной сеткой — контракт не ломается.
    stepLat: resp.step_lat ?? resp.step,
    stepLon: resp.step_lon ?? resp.step,
    rows: resp.rows,
    cols: resp.cols,
    values: resp.values,
  }
}

/** Границы ползунка, раздвинутые НАРУЖУ до сетки 0.5°.

 *  Наружу, а не внутрь: если vmax=19.5, а округлить вниз до 19.0, крайняя тёплая
 *  изотерма поля стала бы недостижима. Плюс гарантируем непустой диапазон на
 *  вырожденном поле (одна температура на весь вьюпорт). */
export function sliderBounds(
  vmin: number | null | undefined,
  vmax: number | null | undefined,
): { min: number; max: number } {
  if (vmin == null || vmax == null) return { min: FALLBACK_MIN, max: FALLBACK_MAX }
  const min = Math.floor(vmin / ISOTHERM_STEP) * ISOTHERM_STEP
  let max = Math.ceil(vmax / ISOTHERM_STEP) * ISOTHERM_STEP
  if (max <= min) max = min + ISOTHERM_STEP
  return { min, max }
}

/** Прижать градус к диапазону и к сетке 0.5°. */
export function clampLevel(level: number, min: number, max: number): number {
  return snapToStep(Math.min(max, Math.max(min, level)))
}

/** Стартовая изотерма: середина диапазона поля, снапнутая к 0.5°. */
export function defaultLevel(
  vmin: number | null | undefined,
  vmax: number | null | undefined,
): number {
  if (vmin == null || vmax == null) return 12 // тёплая рабочая изотерма по умолчанию
  return snapToStep((vmin + vmax) / 2)
}
